import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MEAL_TYPES = [["desayuno", "Desayuno"], ["almuerzo", "Almuerzo"], ["cena", "Cena"], ["snacks", "Snacks"]];
const PORTIONS = [[0.5, "½ porción"], [1, "1 porción"], [1.5, "1½ porciones"], [2, "2 porciones"]];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const AddToMealLogButton = ({ item, defaultMealType = "snacks", approx: rangeApprox = false }) => {
  const approx = rangeApprox || !!item.approx;
  const [open, setOpen] = useState(false);
  const [mealType, setMealType] = useState(defaultMealType);
  const [portions, setPortions] = useState(1);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  if (item.carbs_g == null || item.fats_g == null) {
    return (
      <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-[var(--mep-text-muted)]" data-testid={`macros-pending-${item.id}`}>
        Macros pendientes
      </div>
    );
  }

  const m = (v) => Math.round(v * portions);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/meal-log/preset", {
        meal_type: mealType, date: todayISO(), name: item.name, portions, approx,
        kcal: item.kcal, protein_g: item.protein_g, carbs_g: item.carbs_g, fats_g: item.fats_g,
      });
      setOpen(false);
      toast.success("Añadido a Mi comida.", { action: { label: "Ver", onClick: () => navigate("/mi-comida") } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo añadir");
    } finally {
      setSaving(false);
    }
  };

  const chip = (active) => `px-3 py-1.5 rounded-full border text-xs transition-colors ${active ? "bg-[var(--mep-gold)] text-black border-[var(--mep-gold)]" : "border-[var(--mep-border)] text-[var(--mep-text)] hover:border-[var(--mep-gold)]"}`;

  return (
    <>
      <button
        type="button" onClick={() => { setPortions(1); setMealType(defaultMealType); setOpen(true); }}
        data-testid={`add-to-meal-log-${item.id}`}
        className="mt-4 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-[var(--mep-pink)] text-white text-xs hover:brightness-110"
      >
        <Plus size={14} /> Añadir a Mi comida
      </button>
      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="bg-[var(--mep-surface)] border-[var(--mep-border)] max-w-md" data-testid="add-to-meal-log-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{item.name}</DialogTitle>
            <DialogDescription className="text-xs text-[var(--mep-text-muted)]">Elige el bloque y la cantidad para registrarla hoy.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2">Registrar en</div>
              <div className="flex flex-wrap gap-2">
                {MEAL_TYPES.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setMealType(k)} data-testid={`preset-meal-type-${k}`} className={chip(mealType === k)}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2">Cantidad</div>
              <div className="flex flex-wrap gap-2">
                {PORTIONS.map(([p, l]) => (
                  <button key={p} type="button" onClick={() => setPortions(p)} data-testid={`preset-portion-${p}`} className={chip(portions === p)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[var(--mep-surface-2)] border border-[var(--mep-border)] text-sm" data-testid="preset-macro-preview">
              {m(item.kcal)} kcal · Proteína {m(item.protein_g)} g · Carbohidratos {m(item.carbs_g)} g · Grasas {m(item.fats_g)} g
              {approx && <div className="text-[11px] text-[var(--mep-text-muted)] italic mt-1" data-testid="preset-approx-note">Macros aproximados{rangeApprox ? " (punto medio del rango)" : ""}.</div>}
            </div>
            <button
              type="button" onClick={save} disabled={saving}
              data-testid="preset-save"
              className="w-full py-2.5 rounded-full bg-[var(--mep-pink)] text-white text-sm hover:brightness-110 disabled:opacity-60"
            >
              {saving ? "Guardando…" : "Guardar en Mi comida"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
